import { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Menu } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { Modal } from "../Modal";

const ScrollableMenuItems = ({ open, children }) => {
  const menuReference = useRef(null);
  const [maximumHeight, setMaximumHeight] = useState();

  useLayoutEffect(() => {
    if (!open) return;

    const viewport = window.visualViewport;
    const updateMaximumHeight = () => {
      const viewportBottom = viewport
        ? viewport.offsetTop + viewport.height
        : window.innerHeight;
      const menuTop = menuReference.current.getBoundingClientRect().top;
      setMaximumHeight(Math.max(0, viewportBottom - menuTop - 8));
    };

    updateMaximumHeight();
    window.addEventListener("resize", updateMaximumHeight);
    window.addEventListener("scroll", updateMaximumHeight, true);
    viewport?.addEventListener("resize", updateMaximumHeight);
    viewport?.addEventListener("scroll", updateMaximumHeight);

    return () => {
      window.removeEventListener("resize", updateMaximumHeight);
      window.removeEventListener("scroll", updateMaximumHeight, true);
      viewport?.removeEventListener("resize", updateMaximumHeight);
      viewport?.removeEventListener("scroll", updateMaximumHeight);
    };
  }, [open]);

  return (
    <Menu.Items
      ref={menuReference}
      style={{ maxHeight: maximumHeight }}
      className={`${open ? "block" : "hidden"} absolute left-0 right-auto z-10 mt-2 w-max min-w-full max-w-[calc(100vw-1rem)] origin-top-left divide-y divide-gray-100 overflow-y-auto overscroll-contain rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none lg:left-auto lg:right-0 lg:origin-top-right`}
      static
    >
      {children}
    </Menu.Items>
  );
};

const MenuButton = props => {
  const { label, disabled, options = [] } = props || {};

  return (
    <Menu
      as="div"
      className="relative inline-block max-w-full flex-shrink-0 text-left"
    >
      {({ open }) => (
        <>
          <div>
            <Menu.Button
              disabled={disabled}
              className={`flex w-full items-center justify-center whitespace-nowrap rounded-md border border-transparent bg-black
          px-4 py-2 text-xs font-medium text-white
          ${disabled ? "bg-opacity-70" : "hover:bg-opacity-70"}`}
            >
              {label}
              <ChevronDownIcon
                className="hover:text--100 -mr-1 ml-2 h-4 w-4"
                aria-hidden="true"
              />
            </Menu.Button>
          </div>
          {
            <ScrollableMenuItems open={open}>
              {options?.map((item, index) => {
                return (
                  <div key={index} className="px-1 py-1">
                    <Menu.Item>
                      {({ active }) => {
                        const itemClassName = `${
                          active ? "bg-gray-100 text-black" : "text-gray-900"
                        } ${item?.disabled ? "bg-gray-300" : "hover:bg-gray-50"} group flex w-full truncate whitespace-nowrap rounded-md px-2 py-2 text-left text-sm`;

                        return item?.modalProps ? (
                          <Modal {...item?.modalProps}>
                            <button
                              title={item?.title || item?.label}
                              disabled={item?.disabled}
                              className={itemClassName}
                            >
                              {item?.label}
                            </button>
                          </Modal>
                        ) : item?.href ? (
                          <a
                            href={item?.disabled ? undefined : item?.href}
                            title={item?.title || item?.label}
                            className={itemClassName}
                            target={item?.target}
                            rel={item?.rel}
                            aria-disabled={item?.disabled ? "true" : "false"}
                            onClick={event => {
                              if (item?.disabled) {
                                event.preventDefault();
                              }
                            }}
                            {...item?.linkProps}
                          >
                            {item?.label}
                          </a>
                        ) : (
                          <button
                            title={item?.title || item?.label}
                            disabled={item?.disabled}
                            className={itemClassName}
                            onClick={item?.disabled ? undefined : item?.onClick}
                          >
                            {item?.label}
                          </button>
                        );
                      }}
                    </Menu.Item>
                  </div>
                );
              })}
            </ScrollableMenuItems>
          }
          {/* </Transition> */}
        </>
      )}
    </Menu>
  );
};

MenuButton.propTypes = {
  label: PropTypes.string,
  disabled: PropTypes.bool,
  options: PropTypes.array,
};

export { MenuButton };
